import TextArea from '@/components/TextArea';
import { LabelButton, PrimaryButton } from '@/components/shared/Button';
import { useAuth } from '@/context/AuthContext';
import { InterviewState } from '@/enums/InterviewState';
import { SkillLevel } from '@/enums/SkillLevel';
import { UserRoles } from '@/enums/UsersRole';
import { TechnicalSkillsModel } from '@/models/AnnualInterviewModel';
import { RootState } from '@/state';
import {
  saveAnnualInterviewAsync,
  updateTechnicalSkills,
} from '@/state/slices/annualInterviewSlice';
import { resetForm } from '@/state/slices/employeeSlice';
import { PaperAirplaneIcon } from '@heroicons/react/solid';
import { ThunkDispatch, AnyAction } from '@reduxjs/toolkit';
import { useFormik } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';

const init: TechnicalSkillsModel = {
  id: '',
  annualInterviewId: '',
  masteryAtPosition: SkillLevel.Bien,
  theoreticalKnowledge: SkillLevel.Correct,
  technicalElementsMastery: SkillLevel.Excellent,
  technicalProgressTracking: SkillLevel.Excellent,
  masteryOfRequiredTools: SkillLevel.Excellent,
  comments: '',
  specificAbilities: '',
};

const SkillTable = () => {
  const { user } = useAuth();

  const { t } = useTranslation();
  const techSkills = useSelector(
    (state: RootState) => state.annualInterviews.annualInterview.technicalSkills
  );
  const annualInerview = useSelector(
    (state: RootState) => state.annualInterviews.annualInterview
  );
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();
  const [techSkillLevels, setSkillLevels] = useState(techSkills ?? init);
  const isTeamLeader = annualInerview.responsibleId == user?.id;
  const isEmployee = annualInerview.employeeId == user?.id;
  const disabled = annualInerview.interviewState == InterviewState.Completed;
  const nonNumericKeys = Object.keys(SkillLevel).filter(
    (key) => !isNaN(Number(SkillLevel[key]))
  ) as Array<keyof typeof SkillLevel>;

  const formik = useFormik({
    initialValues: {
      ...init,
      ...techSkills,
    },
    enableReinitialize: true,

    onSubmit: (values) => {
      dispatch(updateTechnicalSkills({ technicalSkills: values }));
    },
  });

  const handleSkillLevelChange = (field: string, value: any) => {
    formik.setFieldValue(field, value);
  };
  const handleSyncData = () => {
    dispatch(saveAnnualInterviewAsync(annualInerview));
  };
  const handleSaveData = () => {
    dispatch(updateTechnicalSkills({ technicalSkills: techSkillLevels }));
  };
  return (
    <form onSubmit={formik.handleSubmit}>
      <div className="mx-2 flex flex-col gap-2">
        <table className="w-full text-left text-sm text-gray-500 dark:text-gray-400">
          <thead className="bg-gray-50 text-xs uppercase text-gray-700 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="px-6 py-3"></th>
              {nonNumericKeys.map((level: string) => (
                <th key={level} scope="col" className="px-6 py-3">
                  {t(`SkillLevel.${level}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.keys(techSkillLevels)
              .filter(
                (objective) =>
                  objective !== 'id' &&
                  objective !== 'annualInterviewId' &&
                  objective !== 'annualInterview' &&
                  objective !== 'specificAbilities' &&
                  objective !== 'comments'
              )
              .map((skill: string) => (
                <tr
                  key={skill}
                  className="border-b bg-white dark:border-gray-700 dark:bg-gray-800"
                >
                  <th scope="row" className="px-6 py-4 font-medium">
                    {t(`annualInterviewForm.TechnicalSkills.${skill}`)}
                  </th>
                  {nonNumericKeys.map((level: keyof typeof SkillLevel) => (
                    <td key={level} className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={formik.values[skill] === SkillLevel[level]}
                        onChange={() =>
                          handleSkillLevelChange(skill, SkillLevel[level])
                        }
                        disabled={!isTeamLeader || disabled}
                      />
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
        <div className="group relative mb-6 w-full">
          <TextArea
            id="specificAbilities"
            name="specificAbilities"
            label={t('annualInterviewForm.TechnicalSkills.specificAbilities')}
            value={formik.values.specificAbilities ?? ''}
            onChange={formik.handleChange}
            disabled={!isTeamLeader || disabled}
          />
        </div>
        <div className="group relative mb-6 w-full">
          <TextArea
            id="comments"
            name="comments"
            label={t('annualInterviewForm.TechnicalSkills.comments')}
            value={formik.values.comments ?? ''}
            onChange={formik.handleChange}
            disabled={isTeamLeader || disabled}
          />
        </div>
        {!disabled && (
          <div className="flex justify-between">
            <LabelButton type="button" onClick={() => resetForm()}>
              {t('annualInterviewForm.TechnicalSkills.reset')}
            </LabelButton>
            <PrimaryButton
              className="border-0 bg-purple-300 dark:bg-purple-500"
              type="button"
              onClick={handleSyncData}
            >
              <div className="flex items-center gap-2">
                <PaperAirplaneIcon className="h-4 w-4 rotate-90" />
                {t('annualInterviewForm.TechnicalSkills.Sync')}
              </div>
            </PrimaryButton>
            <PrimaryButton
              type="submit"
              className="border-0 bg-primary-300 dark:bg-primary-500"
            >
              {t('annualInterviewForm.TechnicalSkills.submit')}
            </PrimaryButton>
          </div>
        )}
      </div>
    </form>
  );
};

export default SkillTable;
